import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Plus, CircleNotch, CheckCircle, Warning, MapPin, Globe, ListChecks, TextIndent, MagnifyingGlass, Sparkle, Database, ArrowsClockwise } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import { plainToHtml } from "@/lib/richtext";
import TagInput from "@/components/TagInput";
import AudienceSelect from "@/components/AudienceSelect";

// Admin tool: discover institutions from a listing URL/topic OR paste specific URLs -> AI drafts an
// original article (+ factual name/address/courses) for each. Review, enrich with media, publish.
export default function ImportInstitutions() {
  const [discoverInput, setDiscoverInput] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [found, setFound] = useState([]);
  const [sweep, setSweep] = useState({ state: "", category: "Engineering colleges", country: "India" });
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState("");
  const [urls, setUrls] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [drafts, setDrafts] = useState([]);
  const [audience, setAudience] = useState([]);
  const [matching, setMatching] = useState(false);
  const [matchList, setMatchList] = useState([]);
  const [ingesting, setIngesting] = useState(false);
  const [ingestProg, setIngestProg] = useState({ done: 0, total: 0 });

  const setForm = (key, patch) =>
    setDrafts((ds) => ds.map((x) => (x.key === key ? { ...x, form: { ...x.form, ...patch } } : x)));

  const discover = async () => {
    const v = discoverInput.trim();
    if (!v) return toast.error("Paste a listing URL or type e.g. 'B.Tech colleges in Chandigarh'");
    setDiscovering(true);
    setFound([]);
    try {
      const body = v.startsWith("http") ? { url: v } : { query: v };
      const { data } = await api.post("/admin/import/discover", body);
      const items = (data.items || []).map((it, i) => ({ ...it, id: `${Date.now()}-${i}`, selected: true }));
      setFound(items);
      if (!items.length) toast.message("No institutions found — try a different listing or topic");
      else toast.success(`Found ${items.length} institutions — pick which to draft`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Discovery failed"); }
    finally { setDiscovering(false); }
  };

  const runImport = async (items) => {
    if (!items.length) return;
    setRunning(true);
    setProgress({ done: 0, total: items.length });
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const key = `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`;
      setDrafts((d) => [{ key, label: it.label, status: "loading" }, ...d]);
      try {
        const { data } = await api.post("/admin/import/institution", it.body);
        setDrafts((d) => d.map((x) => x.key === key ? {
          ...x, status: "ready", facts: data,
          form: { title: data.article_title || data.name, summary: data.article_summary || "", body: plainToHtml(data.article_body || ""), tags: data.tags || [] },
        } : x));
      } catch (e) {
        setDrafts((d) => d.map((x) => x.key === key ? { ...x, status: "error", error: formatApiErrorDetail(e.response?.data?.detail) || "Import failed" } : x));
      }
      setProgress((p) => ({ ...p, done: i + 1 }));
    }
    setRunning(false);
    toast.success("Drafting complete — review & publish below");
  };

  const draftSelected = () => {
    const sel = found.filter((f) => f.selected);
    if (!sel.length) return toast.error("Select at least one institution");
    runImport(sel.map((it) => ({ label: it.name, body: { query: [it.name, it.city, it.state, it.country].filter(Boolean).join(", ") } })));
  };

  // Save to Directory: match discovered institutions against the 83k DB, then enrich/create.
  const saveToDirectory = async () => {
    const sel = found.filter((f) => f.selected);
    if (!sel.length) return toast.error("Select at least one institution");
    setMatching(true); setMatchList([]);
    try {
      const items = sel.map((it) => ({ name: it.name, city: it.city, state: it.state, country: it.country }));
      const { data } = await api.post("/admin/import/match", { items });
      const list = (data.results || []).map((r, i) => ({
        ...r, key: `m-${Date.now()}-${i}`,
        action: !r.match ? "create" : ((r.match.score || 0) >= 0.85 ? "enrich" : "skip"),
        status: "pending",
      }));
      setMatchList(list);
      const dup = list.filter((r) => r.match).length;
      toast.success(`${dup} already in directory · ${list.length - dup} new`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Match failed"); }
    finally { setMatching(false); }
  };

  const setMatchAction = (key, action) => setMatchList((ls) => ls.map((x) => (x.key === key ? { ...x, action } : x)));

  const confirmIngest = async () => {
    const todo = matchList.filter((m) => m.action !== "skip" && m.status !== "done");
    if (!todo.length) return toast.error("Nothing selected to save");
    setIngesting(true); setIngestProg({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i++) {
      const m = todo[i];
      setMatchList((ls) => ls.map((x) => (x.key === m.key ? { ...x, status: "saving" } : x)));
      try {
        const { data } = await api.post("/admin/import/ingest", {
          name: m.name, city: m.city, state: m.state, country: m.country,
          institution_id: m.match?.id || null, action: m.action,
        });
        setMatchList((ls) => ls.map((x) => (x.key === m.key ? { ...x, status: "done", result: data } : x)));
      } catch (e) {
        setMatchList((ls) => ls.map((x) => (x.key === m.key ? { ...x, status: "error", error: formatApiErrorDetail(e.response?.data?.detail) || "Failed" } : x)));
      }
      setIngestProg((p) => ({ ...p, done: i + 1 }));
    }
    setIngesting(false);
    toast.success("Directory updated");
  };

  const mergeIntoFound = (items) => {
    setFound((prev) => {
      const seen = new Set(prev.map((p) => p.name.toLowerCase()));
      const add = (items || []).filter((it) => it.name && !seen.has(it.name.toLowerCase()))
        .map((it) => ({ ...it, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, selected: true }));
      return add.length ? [...prev, ...add] : prev;
    });
  };
  const runSweep = async () => {
    const st = sweep.state.trim();
    if (!st) return toast.error("Enter a state / region");
    const cat = sweep.category.trim() || "Colleges";
    setSweeping(true); setFound([]); setSweepMsg("Finding districts…");
    try {
      const { data } = await api.post("/admin/import/districts", { state: st, country: sweep.country });
      const districts = data.districts || [];
      if (!districts.length) { toast.error("No districts found — try a different name"); setSweeping(false); setSweepMsg(""); return; }
      for (let i = 0; i < districts.length; i++) {
        const dist = districts[i];
        setSweepMsg(`Scanning ${dist} — ${i + 1}/${districts.length}`);
        try {
          const { data: dd } = await api.post("/admin/import/discover", { query: `${cat} in ${dist} district, ${st}, ${sweep.country}`, limit: 15 });
          mergeIntoFound(dd.items || []);
        } catch { /* skip this district */ }
      }
      setSweepMsg("");
      toast.success("Sweep complete — pick institutions to draft");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sweep failed"); }
    finally { setSweeping(false); }
  };
  const importUrls = () => {
    const list = urls.split(/\n+/).map((s) => s.trim()).filter((s) => s.startsWith("http"));
    if (!list.length) return toast.error("Paste at least one institution URL (one per line)");
    runImport(list.map((url) => ({ label: url, body: { url } })));
  };

  const insertFacts = (key) => {
    setDrafts((ds) => ds.map((x) => {
      if (x.key !== key) return x;
      const fa = x.facts || {};
      const loc = [fa.address, [fa.city, fa.state, fa.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
      let html = "<h2>Institution details</h2>";
      if (loc) html += `<p><strong>Location:</strong> ${loc}</p>`;
      if (fa.website) html += `<p><strong>Website:</strong> <a href="${fa.website}" target="_blank" rel="noopener noreferrer">${fa.website}</a></p>`;
      if ((fa.courses || []).length) html += "<p><strong>Programs offered:</strong></p><ul>" + fa.courses.map((c) => `<li>${c}</li>`).join("") + "</ul>";
      return { ...x, form: { ...x.form, body: (x.form.body || "") + html } };
    }));
    toast.success("Details appended to the article body");
  };

  const publish = async (key) => {
    const d = drafts.find((x) => x.key === key);
    if (!d) return;
    if (!d.form.title || !d.form.body) return toast.error("Title and body are required");
    setDrafts((ds) => ds.map((x) => (x.key === key ? { ...x, publishing: true } : x)));
    try {
      await api.post("/articles", { title: d.form.title, summary: d.form.summary, body: d.form.body, tags: d.form.tags, editions: audience });
      setDrafts((ds) => ds.map((x) => (x.key === key ? { ...x, status: "published", publishing: false } : x)));
      toast.success("Article published");
    } catch (e) {
      setDrafts((ds) => ds.map((x) => (x.key === key ? { ...x, publishing: false } : x)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed");
    }
  };

  const selCount = found.filter((f) => f.selected).length;

  return (
    <div className="space-y-6" data-testid="import-institutions-tab">
      {/* State sweep mode */}
      <Card className="border-secondary/30 bg-secondary/5 p-5" data-testid="sweep-card">
        <div className="flex items-center gap-2"><MapPin size={18} weight="duotone" className="text-secondary" /><span className="font-medium">State sweep — every district</span></div>
        <p className="mt-1 text-sm text-muted-foreground">Enter a state/region and a category. We walk <strong>every district</strong> and collect the institutions there — then you bulk-select and draft. Great for building the directory state by state.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <div><Label className="text-xs">State / region</Label><Input value={sweep.state} onChange={(e) => setSweep({ ...sweep, state: e.target.value })} placeholder="e.g. Kerala" className="mt-1" data-testid="sweep-state" /></div>
          <div><Label className="text-xs">Category</Label><Input value={sweep.category} onChange={(e) => setSweep({ ...sweep, category: e.target.value })} placeholder="e.g. Engineering colleges" className="mt-1" data-testid="sweep-category" /></div>
          <div className="flex items-end"><Button onClick={runSweep} disabled={sweeping} className="w-full rounded-full gap-1.5" data-testid="sweep-run">{sweeping ? <CircleNotch size={15} className="animate-spin" /> : <MapPin size={15} />} {sweeping ? "Sweeping…" : "Sweep districts"}</Button></div>
        </div>
        {sweepMsg && <p className="mt-2 text-xs text-secondary">{sweepMsg} · {found.length} found so far</p>}
      </Card>

      {/* Discover mode */}
      <Card className="border-border/60 p-5">
        <div className="flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Discover institutions</span></div>
        <p className="mt-1 text-sm text-muted-foreground">Paste a listing URL (e.g. collegedunia city/state page) or just type a topic like <em>“Engineering colleges in Kerala”</em>. We list the real institutions — you choose which to draft.</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={discoverInput} onChange={(e) => setDiscoverInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && discover()} placeholder="https://collegedunia.com/btech/chandigarh-colleges  —or—  Medical colleges in Tamil Nadu" className="pl-9" data-testid="discover-input" />
          </div>
          <Button onClick={discover} disabled={discovering} className="rounded-full gap-1.5" data-testid="discover-run">
            {discovering ? <CircleNotch size={15} className="animate-spin" /> : <MagnifyingGlass size={15} />} {discovering ? "Finding…" : "Find institutions"}
          </Button>
        </div>
        {found.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <button onClick={() => setFound((f) => f.map((x) => ({ ...x, selected: !(selCount === f.length) })))} className="text-xs text-secondary hover:underline" data-testid="discover-toggle-all">{selCount === found.length ? "Deselect all" : "Select all"}</button>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={saveToDirectory} disabled={matching || ingesting || !selCount} className="rounded-full gap-1.5" data-testid="match-save-btn">{matching ? <CircleNotch size={14} className="animate-spin" /> : <Database size={14} weight="duotone" />} Save {selCount} to directory</Button>
                <Button size="sm" onClick={draftSelected} disabled={running || !selCount} className="rounded-full gap-1.5" data-testid="discover-draft-selected"><Sparkle size={14} weight="fill" /> Draft {selCount} article{selCount === 1 ? "" : "s"}</Button>
              </div>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {found.map((it) => (
                <label key={it.id} className="flex cursor-pointer items-start gap-2 rounded-lg border border-border/60 p-2.5 text-sm hover:border-secondary/40" data-testid={`discover-item-${it.id}`}>
                  <input type="checkbox" checked={it.selected} onChange={() => setFound((f) => f.map((x) => x.id === it.id ? { ...x, selected: !x.selected } : x))} className="mt-1 accent-current" />
                  <span><span className="font-medium">{it.name}</span>{(it.city || it.state) && <span className="block text-xs text-muted-foreground">{[it.city, it.state, it.country].filter(Boolean).join(", ")}</span>}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Match & Ingest: save discovered institutions to the directory (dedupe first) */}
      {matchList.length > 0 && (
        <Card className="border-primary/30 bg-primary/5 p-5" data-testid="match-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><Database size={18} weight="duotone" className="text-primary" /><span className="font-medium">Save to directory — {matchList.length} matched</span></div>
            <Button size="sm" onClick={confirmIngest} disabled={ingesting} className="rounded-full gap-1.5" data-testid="match-confirm-btn">
              {ingesting ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              {ingesting ? `Saving ${ingestProg.done}/${ingestProg.total}…` : `Confirm & save ${matchList.filter((m) => m.action !== "skip" && m.status !== "done").length}`}
            </Button>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Existing entries are <strong>enriched</strong> (blank fields filled + courses added). New ones become fresh, info-rich directory pages with an alumni group.</p>
          <div className="mt-3 space-y-1.5">
            {matchList.map((m) => (
              <div key={m.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-background p-2.5 text-sm" data-testid={`match-row-${m.key}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{m.name}</span>
                    {m.match ? (
                      <span className="inline-flex items-center gap-1">
                        <Badge className={`rounded-full border-transparent text-[11px] font-normal ${(m.match.score || 0) >= 0.85 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid={`match-existing-${m.key}`}>In directory · {Math.round((m.match.score || 0) * 100)}% — {m.match.name}</Badge>
                        {(m.match.score || 0) < 0.85 && <span className="text-[11px] font-medium text-amber-600" data-testid={`match-warn-${m.key}`}>Verify before enriching</span>}
                      </span>
                    ) : (
                      <Badge variant="outline" className="rounded-full text-[11px] font-normal text-secondary" data-testid={`match-new-${m.key}`}>New</Badge>
                    )}
                    {(m.city || m.state) && <span className="text-xs text-muted-foreground">{[m.city, m.state].filter(Boolean).join(", ")}</span>}
                  </div>
                  {m.status === "done" && m.result && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-green-700"><CheckCircle size={13} weight="fill" /> {m.result.status === "enriched" ? `Enriched${(m.result.fields_added || []).length ? " (" + m.result.fields_added.join(", ") + ")" : " (no blanks to fill)"}` : "Created new page"}</div>
                  )}
                  {m.status === "error" && <div className="mt-0.5 flex items-center gap-1 text-xs text-red-600"><Warning size={13} /> {m.error}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {m.status === "saving" && <CircleNotch size={15} className="animate-spin text-muted-foreground" />}
                  {m.status !== "done" && (
                    <div className="flex overflow-hidden rounded-full border border-border/60 text-[11px]">
                      {m.match && <button onClick={() => setMatchAction(m.key, "enrich")} className={`px-2.5 py-1 ${m.action === "enrich" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`match-action-enrich-${m.key}`}>Enrich</button>}
                      <button onClick={() => setMatchAction(m.key, "create")} className={`px-2.5 py-1 ${m.action === "create" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`match-action-create-${m.key}`}>Create new</button>
                      <button onClick={() => setMatchAction(m.key, "skip")} className={`px-2.5 py-1 ${m.action === "skip" ? "bg-muted-foreground/20" : "hover:bg-muted"}`} data-testid={`match-action-skip-${m.key}`}>Skip</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Paste specific URLs */}
      <Card className="border-border/60 p-5">
        <div className="flex items-center gap-2"><DownloadSimple size={18} weight="duotone" className="text-secondary" /><span className="font-medium">Or paste specific institution URLs</span></div>
        <p className="mt-1 text-sm text-muted-foreground">One URL per line. We draft an <strong>original</strong> article from public knowledge for each — enrich with photos/videos, then publish.</p>
        <Textarea rows={3} value={urls} onChange={(e) => setUrls(e.target.value)} placeholder={"https://collegedunia.com/university/25703-iit-bombay-...\nhttps://collegedunia.com/college/..."} className="mt-3 text-sm" data-testid="import-urls" />
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Button onClick={importUrls} disabled={running} className="rounded-full gap-1.5" data-testid="import-run">
            {running ? <CircleNotch size={15} className="animate-spin" /> : <DownloadSimple size={15} />}
            {running ? `Working ${progress.done}/${progress.total}…` : "Import & draft articles"}
          </Button>
          <div className="w-64"><AudienceSelect value={audience} onChange={setAudience} testid="import-audience" label="Publish drafts to" /></div>
          <span className="text-xs text-muted-foreground">Processed one at a time so you see live progress.</span>
        </div>
      </Card>

      {drafts.map((d) => (
        <Card key={d.key} className="border-border/60 p-5" data-testid={`import-draft-${d.key}`}>
          {d.status === "loading" && <div className="flex items-center gap-2 text-sm text-muted-foreground"><CircleNotch size={16} className="animate-spin" /> Researching <span className="truncate">{d.label}</span></div>}
          {d.status === "error" && <div className="flex items-center gap-2 text-sm text-red-600"><Warning size={16} /> {d.error} — <span className="truncate text-muted-foreground">{d.label}</span></div>}
          {d.status === "published" && <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle size={16} weight="fill" /> Published: <strong>{d.form.title}</strong></div>}

          {d.status === "ready" && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-heading text-lg font-medium">{d.facts?.name}</span>
                {(d.facts?.city || d.facts?.state) && <Badge variant="outline" className="rounded-full text-xs font-normal"><MapPin size={11} className="mr-1" />{[d.facts.city, d.facts.state, d.facts.country].filter(Boolean).join(", ")}</Badge>}
                {d.facts?.website && <a href={d.facts.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-secondary hover:underline"><Globe size={12} />{d.facts.website.replace(/^https?:\/\//, "")}</a>}
              </div>
              {(d.facts?.courses || []).length > 0 && (
                <div className="rounded-xl bg-muted/40 p-3 text-xs" data-testid={`import-courses-${d.key}`}>
                  <div className="mb-1 flex items-center gap-1 font-medium text-muted-foreground"><ListChecks size={13} /> Courses / programs</div>
                  <div className="flex flex-wrap gap-1.5">{d.facts.courses.map((c, i) => <Badge key={i} variant="secondary" className="rounded-full font-normal">{c}</Badge>)}</div>
                </div>
              )}
              <div><Label>Article title</Label><Input className="mt-1" value={d.form.title} onChange={(e) => setForm(d.key, { title: e.target.value })} data-testid={`import-title-${d.key}`} /></div>
              <div><Label>Summary</Label><Input className="mt-1" value={d.form.summary} onChange={(e) => setForm(d.key, { summary: e.target.value })} data-testid={`import-summary-${d.key}`} /></div>
              <div><Label>Tags</Label><TagInput value={d.form.tags} onChange={(tags) => setForm(d.key, { tags })} suggestions={[]} testid={`import-tags-${d.key}`} /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Body <span className="font-normal text-muted-foreground">· embed photos & videos with the toolbar</span></Label>
                  <button type="button" onClick={() => insertFacts(d.key)} className="inline-flex items-center gap-1 text-xs text-secondary hover:underline" data-testid={`import-insert-facts-${d.key}`}><TextIndent size={13} /> Append details</button>
                </div>
                <div className="mt-1.5"><RichTextEditor value={d.form.body} onChange={(v) => setForm(d.key, { body: v })} placeholder="Article body…" testid={`import-rte-${d.key}`} /></div>
              </div>
              <Button onClick={() => publish(d.key)} disabled={d.publishing} className="rounded-full gap-1.5" data-testid={`import-publish-${d.key}`}>
                <Plus size={15} /> {d.publishing ? "Publishing…" : "Publish article"}
              </Button>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
